import { isBoolean, isUndefined, isUnion } from "generic-type-guard";

import {
	ConfigurationValue,
	FachverfahrenManifest,
	MappingConfiguration,
	MappingElement,
	ReadonlyCategory,
	TextbausteinData
} from "../app/slices/fachverfahren/types";
import {
	Clients,
	ModuleInfo,
	Roles,
	RuntimeModuleRoleScopeSpec,
	RuntimeModuleRoleSpec,
	RuntimeModuleSpec
} from "../app/slices/manifest/types";
import { StammdatenManifest } from "../app/slices/stammdaten/types";

import { isArrayOf, isNull, isNumber, isObject, isObjectOf, isOptionalOf, isOr, isString } from "./typeGuardUtils";

interface MappingDokument {
	root: FachverfahrenManifest;
}

const isModels = isObjectOf<FachverfahrenManifest["models"]>({
	appModelId: isString,
	mainDocumentModelId: isString,
	overviewModelId: isString
});

const isIdentification = isObjectOf<FachverfahrenManifest["identification"]>({
	key: isString,
	version: isNumber,
	id: isString,
	shortName: isString,
	longName: isString
});

const isPathEntry = isObjectOf<{
	readonly elementName: string;
	readonly index: number;
}>({
	elementName: isString,
	index: isNumber
});

const isMappingElement = isObjectOf<MappingElement>({
	alias: isString,
	path: isArrayOf(isPathEntry)
});

export const isConfigurationValue = isObjectOf<ConfigurationValue>({
	key: isString,
	value: isString
});

const isRuntimeModuleRoleScope = isObjectOf<RuntimeModuleRoleScopeSpec>({
	name: isString,
	weight: isNumber
});

const isRuntimeModuleRole = isObjectOf<RuntimeModuleRoleSpec>({
	name: isString,
	scopes: isUnion(isArrayOf(isRuntimeModuleRoleScope), isUndefined)
});

export const isRuntimeModuleSpec = isObjectOf<RuntimeModuleSpec>({
	name: isString,
	roles: isUnion(isArrayOf(isRuntimeModuleRole), isUndefined)
});

export const isTextbausteinReadonlyChild = isObjectOf<ReadonlyCategory>({
	name: isString,
	marker: isString
});

export const isTextbausteinData = isObjectOf<TextbausteinData>({
	name: isString,
	marker: isString,
	children: isOptionalOf(isArrayOf(isTextbausteinReadonlyChild))
});

export const isModuleInfo = isObjectOf<ModuleInfo>({
	id: isString,
	version: isString,
	subModules: isString
});

export const isBuildInfo = isObjectOf<FachverfahrenManifest["buildInfo"]>({
	generatorVersion: isString,
	modelSourceRepoVersion: isOr(isString, isNull),
	module: isModuleInfo
});

export const isClient = isObjectOf<Clients>({
	key: isString
});

export const isRole = isObjectOf<Roles>({
	role: isString,
	description: isString
});

export const isStammdatenManifest = isObjectOf<StammdatenManifest>({
	identification: isIdentification,
	models: isModels,
	buildInfo: isBuildInfo,
	runtimeModules: isOptionalOf(isArrayOf(isRuntimeModuleSpec)),
	clients: isOptionalOf(isArrayOf(isClient)),
	textbausteinData: isOptionalOf(isTextbausteinData),
	roles: isOptionalOf(isArrayOf(isRole)),
	persoenlicheLinksVersenden: isOptionalOf(isBoolean)
});

export const isMappingConfiguration = isObjectOf<MappingConfiguration>({
	externalPathSource: isString,
	mappingConfigurationId: isString,
	pathToDocumentReference: isOptionalOf(isOr(isString, isNull))
});

export const isFachverfahrenManifest = isObjectOf<FachverfahrenManifest>({
	identification: isIdentification,
	models: isModels,
	mappingElement: isArrayOf(isMappingElement),
	configurationValues: isArrayOf(isConfigurationValue),
	runtimeModules: isArrayOf(isRuntimeModuleSpec),
	textbausteinData: isOptionalOf(isTextbausteinData),
	mappingConfigurations: isOptionalOf(isArrayOf(isMappingConfiguration)),
	clients: isOptionalOf(isArrayOf(isClient)),
	roles: isOptionalOf(isArrayOf(isRole)),
	buildInfo: isBuildInfo,
	processDefinitionId: isString,
	persoenlicheLinksVersenden: isOptionalOf(isBoolean)
});

export const isFachverfahrenManifestDocument = isObjectOf<MappingDokument>({
	root: isFachverfahrenManifest
});

export const isDocumentWithRoot = isObjectOf<{ root: object }>({
	root: isObject
});
